import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { ChevronDown, ArrowRight, Linkedin, Facebook, Link2 } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';
import { Reveal } from '../components/Reveal';
import { breadcrumbSchema, faqSchema } from '../lib/schema';

/*
 * Journal · CS25 — A Capsule House for Resort Hospitality in India
 * URL: /journal/cs25-capsule-house-india-resorts
 *
 * Editorial long-form article. Every visual asset below is a real, first-party
 * Modularoom photograph of the actual CS25. Do not swap for stock imagery.
 */

const A = 'https://customer-assets-gfyr7b9c.emergentagent.net/job_modularoom-design/artifacts/';
const IMG = {
  exterior:  `${A}su1kq50b_Capsule%20House%20MODULAROOM%20CS25%20Exterior.jpeg`,
  nature:    `${A}djnn6f5q_Capsule%20House%20MODULAROOM%20CS25%20in%20nature.jpeg`,
  bathroom:  `${A}7glazohs_Capsule%20House%20MODULAROOM%20CS25%20Bathroom.jpeg`,
  bedroomWip:`${A}qrfcm0fv_Capsule%20House%20MODULAROOM%20CS25%20Bedroom%20WIP.jpeg`,
  crane:     `${A}qt6ytceb_Capsule%20House%20MODULAROOM%20CS25%20crane.jpeg`,
};

const ARTICLE_URL = 'https://modularoom.com/journal/cs25-capsule-house-india-resorts';
const PUBLISHED = '2026-08-14';
const MODIFIED  = '2026-08-14';
const PUBLISHED_HUMAN = 'August 14, 2026';
const AUTHOR_NAME = 'Jeet Pipalia';
const AUTHOR_ROLE = 'Founder, Modularoom';
const AUTHOR_LINKEDIN = 'https://www.linkedin.com/in/jeetpipalia/';

const FAQS = [
  { q: 'What is a capsule house in India?',
    a: 'A capsule house is a compact, self-contained architectural unit designed for living or hospitality. In resort applications, it can function as independent guest accommodation containing spaces such as a bedroom, bathroom and private outdoor area.' },
  { q: 'What is a capsule house resort?',
    a: 'A capsule house resort uses independent capsule-style accommodation units distributed across a site instead of relying entirely on a conventional hotel building.' },
  { q: 'Is CS25 a capsule house?',
    a: "CS25 is Modularoom's Capsule Suite designed specifically for hospitality. It provides one bedroom, one bathroom and a private balcony within a compact modular accommodation unit." },
  { q: 'Can capsule houses be used for resorts in India?',
    a: 'Yes, where the proposed development is legally permissible and the units satisfy applicable structural, building, fire, environmental and planning requirements.' },
  { q: 'Can capsule houses be built in national parks?',
    a: 'Not automatically. Modular construction does not bypass wildlife or environmental regulations. Protected areas can have strict restrictions and specific approval requirements.' },
  { q: 'Can capsule houses be used for beachfront resorts?',
    a: 'Potentially, where the proposed development is permissible under applicable coastal regulations and planning frameworks. Prefabrication does not exempt a project from CRZ requirements.' },
];

const articleSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  mainEntityOfPage: { '@type': 'WebPage', '@id': ARTICLE_URL },
  headline: 'CS25: A Capsule House for Resort Hospitality in India',
  description:
    "Meet CS25, Modularoom's one-bedroom Capsule Suite with a private bathroom and balcony, designed for resorts, retreats and destination hospitality in India.",
  image: [IMG.exterior, IMG.nature, IMG.crane],
  datePublished: PUBLISHED,
  dateModified: MODIFIED,
  author: {
    '@type': 'Person',
    name: AUTHOR_NAME,
    sameAs: [AUTHOR_LINKEDIN],
  },
  publisher: {
    '@type': 'Organization',
    name: 'MODULAROOM',
    url: 'https://modularoom.com',
    logo: {
      '@type': 'ImageObject',
      url: 'https://modularoom.com/logo.png',
    },
  },
  articleSection: 'Hospitality & Architecture',
  keywords: 'capsule house in India, capsule house for resorts, capsule resort India, modular hospitality, prefab resort rooms, modular resort rooms, resort accommodation India, CS25, Modularoom',
});

// ————————————————————————————————————————————————————————
// Small editorial primitives (kept in-file to preserve the site design system)
// ————————————————————————————————————————————————————————
// Semantic H2 for each numbered section. The visible display headline that follows
// is intentionally a <h3> so the article has clean `H1 → H2 (per section) → H3` hierarchy.
const SectionNumber = ({ n, label }) => (
  <h2 className="flex items-baseline gap-6 mb-10 font-normal">
    <span className="font-display text-[64px] md:text-[92px] leading-none tracking-tight text-black/10 tabular-nums">
      {String(n).padStart(2, '0')}
    </span>
    <span className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">
      <span className="sr-only">{String(n).padStart(2, '0')} — </span>{label}
    </span>
  </h2>
);

const PullQuote = ({ children }) => (
  <div className="max-w-3xl border-l-[3px] border-[#C7A36A] pl-6 md:pl-8 py-2">
    <p className="font-display text-2xl md:text-4xl leading-[1.15] tracking-tight text-black/85 italic">
      {children}
    </p>
  </div>
);

const Caption = ({ children }) => (
  <p className="mt-4 text-[11px] tracking-[0.18em] uppercase text-black/45 leading-[1.7]">{children}</p>
);

// ————————————————————————————————————————————————————————
// Social share bar — LinkedIn / WhatsApp / Facebook / Copy link
// Restrained, editorial. Uses lucide icons + one custom WhatsApp SVG (lucide has no brand).
// ————————————————————————————————————————————————————————
const WhatsAppIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" strokeWidth="1.6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.52 3.48A11.94 11.94 0 0 0 12.05 0C5.5 0 .2 5.3.2 11.85c0 2.09.55 4.13 1.6 5.94L0 24l6.35-1.67a11.87 11.87 0 0 0 5.7 1.45h.01c6.55 0 11.85-5.3 11.85-11.85 0-3.16-1.23-6.13-3.39-8.45zM12.05 21.6h-.01a9.75 9.75 0 0 1-4.97-1.36l-.36-.21-3.77.99 1-3.67-.23-.38a9.72 9.72 0 0 1-1.5-5.19c0-5.38 4.38-9.75 9.77-9.75 2.61 0 5.06 1.02 6.9 2.86a9.7 9.7 0 0 1 2.86 6.9c0 5.38-4.38 9.75-9.75 9.75zm5.35-7.3c-.29-.15-1.72-.85-1.99-.95-.27-.1-.46-.14-.66.14-.19.29-.75.95-.92 1.15-.17.19-.34.22-.63.07-.29-.14-1.24-.46-2.36-1.46-.87-.78-1.46-1.74-1.63-2.03-.17-.29-.02-.45.13-.6.13-.13.29-.34.44-.51.15-.17.19-.29.29-.48.1-.19.05-.36-.02-.51-.07-.15-.66-1.58-.9-2.17-.24-.57-.48-.5-.66-.51h-.56c-.19 0-.51.07-.77.36-.26.29-1.02.99-1.02 2.42s1.05 2.81 1.19 3c.15.19 2.06 3.15 5 4.42.7.3 1.24.48 1.66.61.7.22 1.34.19 1.84.12.56-.08 1.72-.7 1.96-1.38.24-.68.24-1.26.17-1.38-.07-.12-.27-.19-.56-.34z" fill="currentColor" stroke="none"/>
  </svg>
);

function ShareBar({ url, title, testid = 'share-bar', variant = 'light' }) {
  const enc = encodeURIComponent;
  const shareUrl = url;
  const shareText = `${title} — Modularoom Journal`;

  const links = [
    { key: 'linkedin', label: 'Share on LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`, Icon: Linkedin },
    { key: 'whatsapp', label: 'Share on WhatsApp', href: `https://wa.me/?text=${enc(`${shareText} ${shareUrl}`)}`, Icon: WhatsAppIcon },
    { key: 'facebook', label: 'Share on Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`, Icon: Facebook },
  ];

  const copy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        // Fallback for older browsers / non-secure contexts
        const ta = document.createElement('textarea');
        ta.value = shareUrl;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (!ok) throw new Error('copy failed');
      }
      toast.success('Link copied to clipboard.');
    } catch {
      toast.error('Could not copy the link.');
    }
  };

  const dark = variant === 'dark';
  const base = dark
    ? 'text-white/60 hover:text-[#C7A36A] border-white/20 hover:border-[#C7A36A]'
    : 'text-black/55 hover:text-[#C7A36A] border-black/15 hover:border-[#C7A36A]';
  const label = dark ? 'text-white/45' : 'text-black/40';

  return (
    <div data-testid={testid} className="flex items-center gap-3 md:gap-4 flex-wrap">
      <span className={`text-[10px] tracking-[0.28em] uppercase ${label}`}>Share</span>
      {links.map(({ key, label: aria, href, Icon }) => (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={aria}
          data-testid={`${testid}-${key}`}
          className={`inline-flex items-center justify-center w-9 h-9 rounded-full border transition-colors ${base}`}
        >
          <Icon size={15} strokeWidth={1.5} />
        </a>
      ))}
      <button
        type="button"
        onClick={copy}
        aria-label="Copy link"
        data-testid={`${testid}-copy`}
        className={`inline-flex items-center justify-center w-9 h-9 rounded-full border transition-colors ${base}`}
      >
        <Link2 size={15} strokeWidth={1.5} />
      </button>
    </div>
  );
}

// ————————————————————————————————————————————————————————
// Anatomy overlay (Section 01) — restrained callouts on the real exterior
// ————————————————————————————————————————————————————————
const AnatomyPin = ({ top, left, label, align = 'right' }) => (
  <div
    className="absolute hidden md:flex items-center gap-3 text-white"
    style={{ top: `${top}%`, left: `${left}%`, transform: align === 'right' ? 'translateX(0)' : 'translateX(-100%)' }}
  >
    {align === 'left' && <span className="text-[10px] tracking-[0.28em] uppercase whitespace-nowrap">{label}</span>}
    <span className="w-10 border-t border-white/70" />
    <span className="w-2 h-2 rounded-full bg-[#C7A36A] shadow-[0_0_0_4px_rgba(199,163,106,0.25)]" />
    {align === 'right' && <span className="text-[10px] tracking-[0.28em] uppercase whitespace-nowrap">{label}</span>}
  </div>
);

// ————————————————————————————————————————————————————————
// Two-approaches infographic (Section 02)
// ————————————————————————————————————————————————————————
const ApproachDiagram = ({ title, steps, mono }) => (
  <div className={`p-8 md:p-10 ${mono ? 'bg-[#111] text-white' : 'bg-white text-black border border-black/10'}`}>
    <div className={`text-[10px] tracking-[0.32em] uppercase ${mono ? 'text-[#C7A36A]' : 'text-black/45'}`}>{title}</div>
    <div className="mt-8 space-y-4">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`text-[15px] md:text-[17px] leading-snug ${mono ? 'text-white/90' : 'text-black/85'}`}>{s}</div>
          {i < steps.length - 1 && (
            <ChevronDown size={16} strokeWidth={1} className={mono ? 'text-white/30' : 'text-black/25'} />
          )}
        </React.Fragment>
      ))}
    </div>
  </div>
);

// ————————————————————————————————————————————————————————
// Spec row (Section 03 · At a glance)
// ————————————————————————————————————————————————————————
const SpecRow = ({ k, v }) => (
  <div className="grid grid-cols-[140px_1fr] md:grid-cols-[180px_1fr] gap-6 py-4 border-b border-black/10">
    <div className="text-[10px] tracking-[0.28em] uppercase text-black/45">{k}</div>
    <div className="text-[15px] md:text-[16px] text-black/85">{v}</div>
  </div>
);

// ————————————————————————————————————————————————————————
// Six destinations chip strip (Section 04)
// ————————————————————————————————————————————————————————
const DESTINATIONS = [
  { name: 'Mountain', note: 'Frame dramatic views and create private retreats.' },
  { name: 'Forest',   note: 'Independent accommodation immersed in a natural setting.' },
  { name: 'Lake',     note: 'Orient the bedroom and balcony toward the water.' },
  { name: 'Farm',     note: 'Premium accommodation for farm and rural hospitality.' },
  { name: 'Desert',   note: 'Self-contained destination stays in expansive landscapes.' },
  { name: 'Coast',    note: 'Private hospitality where development is legally permissible.' },
];

// ————————————————————————————————————————————————————————
// Comparison table (Section 08)
// ————————————————————————————————————————————————————————
const COMPARE = [
  ['Manufacturing',      'Primarily on site',           'Significant factory production'],
  ['Site labour',        'Extensive',                   'Reduced for many activities'],
  ['Repeatability',      'Rebuilt for each room',       'Product-based'],
  ['Quality control',    'Site dependent',              'Controlled manufacturing processes'],
  ['Remote logistics',   'Multiple material deliveries','Coordinated unit transportation'],
  ['Phased development', 'Possible',                    'Naturally suited to repeatable units'],
  ['Site adaptation',    'Highly flexible',             'Product + site planning'],
  ['Guest experience',   'Design dependent',            'Design dependent'],
];

// ————————————————————————————————————————————————————————
// FAQ accordion (Section 18)
// ————————————————————————————————————————————————————————
const FaqItem = ({ q, a, idx }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border-b border-black/10" data-testid={`journal-cs25-faq-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-baseline justify-between gap-6 py-6 md:py-7 text-left"
      >
        <span className="font-display text-xl md:text-[26px] tracking-tight text-black/90 leading-[1.25]">{q}</span>
        <span className={`shrink-0 text-[#C7A36A] transition-transform duration-500 ${open ? 'rotate-180' : ''}`}>
          <ChevronDown size={20} strokeWidth={1.5} />
        </span>
      </button>
      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-500 ease-out ${open ? 'max-h-64 opacity-100 pb-8' : 'max-h-0 opacity-0'}`}
      >
        <p className="max-w-3xl text-[15px] leading-[1.85] text-black/65">{a}</p>
      </div>
    </div>
  );
};

// ————————————————————————————————————————————————————————
// PAGE
// ————————————————————————————————————————————————————————
export default function JournalCS25() {
  const crumbs = [
    { label: 'Home', to: '/' },
    { label: 'Journal', to: '/journal' },
    { label: 'Hospitality & Architecture' },
    { label: 'CS25: A Capsule House for Resort Hospitality in India' },
  ];
  const bcSchema = breadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Journal', url: '/journal' },
    { name: 'Hospitality & Architecture', url: '/journal' },
    { name: 'CS25: A Capsule House for Resort Hospitality in India', url: '/journal/cs25-capsule-house-india-resorts' },
  ]);

  return (
    <main data-testid="journal-cs25-article" className="bg-white text-[#111]">
      <Helmet>
        <title>CS25 Capsule House in India for Resorts | Modularoom</title>
        <meta name="description" content="Meet CS25, Modularoom's one-bedroom Capsule Suite with a private bathroom and balcony, designed for resorts, retreats and destination hospitality in India." />
        <meta name="keywords" content="capsule house in India, capsule house for resorts, capsule house resort, capsule resort India, modular hospitality, prefab resort rooms, modular resort rooms, resort accommodation India, CS25, Modularoom Capsule Suite" />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="CS25: A Capsule House for Resort Hospitality in India" />
        <meta property="og:description" content="One bedroom. One bathroom. One private balcony. A modular accommodation product designed for destination hospitality." />
        <meta property="og:image" content={IMG.exterior} />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta property="article:published_time" content={PUBLISHED} />
        <meta property="article:section" content="Hospitality & Architecture" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={IMG.exterior} />
        <script type="application/ld+json">{JSON.stringify(articleSchema())}</script>
        <script type="application/ld+json">{JSON.stringify(bcSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema(FAQS))}</script>
      </Helmet>

      {/* ————————————————— HERO ————————————————— */}
      <section data-testid="cs25-hero" className="relative w-full bg-black text-white overflow-hidden" style={{ minHeight: '92svh' }}>
        <img
          src={IMG.exterior}
          alt="Modularoom CS25 Capsule Suite exterior — a compact modular guest accommodation with angled glazing and private balcony"
          width="2560"
          height="1440"
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/10 to-black/70" />
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-16 md:pb-24 min-h-[92svh] flex flex-col justify-between">
          <div className="text-[10px] md:text-[11px] tracking-[0.36em] uppercase text-[#C7A36A]">
            Modularoom Journal · Hospitality &amp; Architecture
          </div>
          <div className="max-w-4xl">
            <div className="text-[11px] tracking-[0.32em] uppercase text-white/70 mb-6">MODULAROOM CS25</div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              A Capsule House for<br/>
              <span className="italic text-white/70">Resort Hospitality in India.</span>
            </h1>
            <p className="mt-8 max-w-xl text-[15px] md:text-[17px] leading-[1.75] text-white/75">
              One bedroom. One bathroom. One private balcony.<br className="hidden md:block" />
              A modular accommodation product designed for destination hospitality.
            </p>

            {/* Byline — real, named author. Only "Jeet Pipalia" is clickable. */}
            <p
              data-testid="article-byline"
              className="mt-8 text-[11px] tracking-[0.24em] uppercase text-white/70"
            >
              By{' '}
              <a
                href={AUTHOR_LINKEDIN}
                target="_blank"
                rel="noopener noreferrer author"
                data-testid="article-author-link"
                className="underline decoration-[#C7A36A]/70 underline-offset-[6px] decoration-[1.5px] text-white hover:text-[#C7A36A] transition-colors"
              >
                {AUTHOR_NAME}
              </a>
              <span aria-hidden="true"> · </span>
              <span>{AUTHOR_ROLE}</span>
              <span aria-hidden="true"> · </span>
              <span>
                Published{' '}
                <time dateTime={PUBLISHED}>{PUBLISHED_HUMAN}</time>
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* ————————————————— BREADCRUMB ————————————————— */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pt-10 md:pt-14">
        <Breadcrumbs items={crumbs} />
      </section>

      {/* ————————————————— INTRODUCTION ————————————————— */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-40">
        <Reveal>
          <div className="max-w-3xl space-y-8 text-[17px] md:text-[19px] leading-[1.8] text-black/75">
            <p>
              India&apos;s most memorable resorts are often found in places where conventional construction is
              least convenient — mountains, forests, coastlines, farms and remote landscapes.
            </p>
            <p>
              This is creating new interest in <strong className="text-black/90 font-normal">capsule houses in India</strong> as
              an accommodation format for destination hospitality.
            </p>
            <p>
              Unlike a conventional hotel room, a purpose-designed capsule house can function as an
              independent guest suite, bringing together a bedroom, bathroom and private outdoor space
              within a compact architectural unit.
            </p>
            <p>
              Modularoom&apos;s <Link to="/products/cs25" className="text-black underline decoration-[#C7A36A]/60 underline-offset-[6px] decoration-[1.5px] hover:text-[#C7A36A] transition-colors">CS25</Link> takes
              this approach specifically for hospitality: one bedroom, one bathroom and a private balcony,
              designed as a repeatable accommodation product for resorts, retreats and experiential stays.
            </p>

            {/* Share strip — subtle, editorial */}
            <div className="pt-4">
              <ShareBar
                url={ARTICLE_URL}
                title="CS25: A Capsule House for Resort Hospitality in India"
                testid="share-top"
                variant="light"
              />
            </div>
          </div>
        </Reveal>
      </section>

      {/* ————————————————— 01 · WHAT IS A CAPSULE HOUSE ————————————————— */}
      <section className="bg-[#FAF8F4] py-24 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <SectionNumber n={1} label="What is a capsule house?" />
            <h3 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02] max-w-3xl">
              A compact, self-contained<br/>
              <span className="italic text-black/40">architectural unit.</span>
            </h3>
          </Reveal>

          <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
            <Reveal className="md:col-span-5">
              <div className="space-y-6 text-[16px] leading-[1.85] text-black/70">
                <p>A capsule house is a compact, self-contained architectural unit designed around the essential functions of living or hospitality.</p>
                <p>For a resort, that can mean:</p>
                <p className="font-display text-2xl md:text-3xl tracking-tight text-black/90 not-italic">Bedroom + Bathroom + Balcony + View</p>
                <p>Instead of placing every guest room inside one large building, individual capsule houses can be positioned across a landscape.</p>
                <p>The result is a different way of thinking about accommodation. The room becomes an independent destination rather than simply a room inside a building.</p>
                <p>This makes capsule houses particularly interesting for destination hospitality, where the surrounding landscape is often as important as the accommodation itself.</p>
              </div>
            </Reveal>

            <Reveal className="md:col-span-7">
              <div>
                <div className="relative w-full aspect-[4/3] bg-black overflow-hidden">
                  <img
                    src={IMG.exterior}
                    alt="Anatomy of a resort capsule house — Modularoom CS25 Capsule Suite exterior with private balcony and view-oriented glazing"
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/15" />
                  <AnatomyPin top={22} left={62} label="Roof overhang" align="right" />
                  <AnatomyPin top={40} left={42} label="View-oriented glazing" align="right" />
                  <AnatomyPin top={64} left={68} label="Private balcony" align="right" />
                  <AnatomyPin top={54} left={22} label="Compact modular envelope" align="left" />
                </div>
                <Caption>Anatomy of a resort capsule house · Modularoom CS25 · exterior</Caption>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ————————————————— 02 · WHY HOSPITALITY ————————————————— */}
      <section className="py-24 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <SectionNumber n={2} label="Why hospitality?" />
            <h3 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02] max-w-3xl">
              Traditional hotels optimise a building.<br/>
              <span className="italic text-black/40">Resorts optimise an experience.</span>
            </h3>
          </Reveal>

          <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-start">
            <Reveal className="md:col-span-6">
              <div className="space-y-6 text-[16px] leading-[1.85] text-black/70">
                <p>A city hotel may benefit from stacking rooms vertically and maximising floor area. A destination resort can have a very different priority:</p>
                <ul className="pt-2 space-y-2 text-[15px] tracking-[0.06em] text-black/70">
                  <li>· Privacy.</li>
                  <li>· Views.</li>
                  <li>· Landscape.</li>
                  <li>· Outdoor living.</li>
                  <li>· Quiet.</li>
                  <li>· Connection with nature.</li>
                </ul>
                <p>Capsule houses allow accommodation to be distributed across a site rather than concentrated entirely within one structure.</p>
                <p>This can give architects and developers greater freedom to position accommodation according to views, terrain, vegetation, privacy and the character of the site.</p>
              </div>
            </Reveal>

            <Reveal className="md:col-span-6">
              <div>
                <div className="text-[10px] tracking-[0.32em] uppercase text-black/45 mb-6">
                  Two approaches to resort accommodation
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <ApproachDiagram
                    title="Conventional Hotel"
                    steps={['One large building', 'Corridors', 'Multiple rooms']}
                  />
                  <ApproachDiagram
                    mono
                    title="Capsule Resort"
                    steps={['Independent suites', 'Landscape', 'Shared hospitality spaces']}
                  />
                </div>
                <Caption>Distributed accommodation vs. centralised construction.</Caption>
              </div>
            </Reveal>
          </div>

          <Reveal>
            <div className="mt-24 md:mt-32">
              <PullQuote>
                Standardise the product. Personalise the placement.
              </PullQuote>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ————————————————— 03 · MEET CS25 ————————————————— */}
      <section className="bg-[#111] text-white py-24 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="flex items-baseline gap-6 mb-10 font-normal">
              <span className="font-display text-[64px] md:text-[92px] leading-none tracking-tight text-white/10 tabular-nums">03</span>
              <span className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">
                <span className="sr-only">03 — </span>Meet CS25
              </span>
            </h2>
            <h3 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] max-w-4xl">
              A Capsule Suite<br/><span className="italic text-white/50">designed for hospitality.</span>
            </h3>
            <div className="mt-8 flex items-baseline gap-6 flex-wrap">
              <div className="font-mono text-[13px] tracking-[0.24em] text-white/60">CS25</div>
              <div className="text-[13px] md:text-[15px] text-white/75">1 Bedroom · 1 Bathroom · Private Balcony</div>
            </div>
          </Reveal>

          <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
            <Reveal className="md:col-span-5">
              <div className="space-y-6 text-[16px] leading-[1.85] text-white/75">
                <p>CS25 is Modularoom&apos;s hospitality-focused Capsule Suite, designed as an independent accommodation unit for resorts and destination-led hospitality.</p>
                <p>Its proposition is deliberately simple: a complete private guest experience within a compact, repeatable architectural product.</p>
                <p>The bedroom provides the core accommodation. The bathroom provides the essential hospitality infrastructure. The balcony extends the experience outdoors. And the surrounding landscape becomes part of the stay.</p>
              </div>
            </Reveal>

            {/* Editorial gallery: 1 large + 2 supporting */}
            <div className="md:col-span-7 grid grid-cols-6 gap-4">
              <Reveal className="col-span-6">
                <div>
                  <div className="relative w-full aspect-[16/10] overflow-hidden">
                    <img src={IMG.exterior} loading="lazy" alt="Modularoom CS25 Capsule Suite exterior with private balcony" className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                  <p className="mt-3 text-[11px] tracking-[0.18em] uppercase text-white/45">CS25 · Completed exterior</p>
                </div>
              </Reveal>
              <Reveal className="col-span-3">
                <div>
                  <div className="relative w-full aspect-[4/5] overflow-hidden">
                    <img src={IMG.bathroom} loading="lazy" alt="CS25 Capsule Suite private bathroom — hospitality-grade fittings inside a modular accommodation unit" className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                  <p className="mt-3 text-[11px] tracking-[0.18em] uppercase text-white/45">CS25 · Private bathroom</p>
                </div>
              </Reveal>
              <Reveal className="col-span-3">
                <div>
                  <div className="relative w-full aspect-[4/5] overflow-hidden">
                    <img src={IMG.bedroomWip} loading="lazy" alt="CS25 Capsule Suite bedroom interior during manufacturing and finishing" className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                  <p className="mt-3 text-[11px] tracking-[0.18em] uppercase text-white/45">CS25 interior during manufacturing and finishing.</p>
                </div>
              </Reveal>
            </div>
          </div>

          {/* At a glance */}
          <Reveal>
            <div className="mt-24 md:mt-32">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A] mb-8">CS25 at a glance</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 border-t border-white/15">
                <div className="[&>div]:border-white/15 [&>div]:!py-5">
                  <SpecRow k="Product" v="Capsule Suite" />
                  <SpecRow k="Model" v="CS25" />
                  <SpecRow k="Accommodation" v="1 Bedroom" />
                  <SpecRow k="Bathroom" v="1" />
                </div>
                <div className="[&>div]:border-white/15 [&>div]:!py-5">
                  <SpecRow k="Outdoor space" v="Private Balcony" />
                  <SpecRow k="Primary application" v="Resort Hospitality" />
                  <SpecRow k="Other applications" v="Retreats · Glamping · Farm Stays" />
                  <SpecRow k="Family" v={<Link to="/collections" className="underline decoration-[#C7A36A]/70 underline-offset-4 hover:text-[#C7A36A] transition-colors">Capsule Suites</Link>} />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ————————————————— 04 · ONE CS25 · MANY DESTINATIONS ————————————————— */}
      <section className="py-24 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <SectionNumber n={4} label="One CS25. Many destinations." />
            <h3 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02] max-w-3xl">
              The strongest opportunity for a capsule house<br/>
              <span className="italic text-black/40">isn&apos;t the city.</span>
            </h3>
          </Reveal>

          <Reveal>
            <div className="mt-16">
              <div className="relative w-full aspect-[21/9] overflow-hidden">
                <img
                  src={IMG.nature}
                  alt="Modularoom CS25 Capsule Suite in a natural destination setting — modular resort accommodation for retreats"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <Caption>CS25 in a natural setting · destination hospitality.</Caption>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-16 md:mt-24 max-w-2xl text-[16px] leading-[1.85] text-black/70">
              <p>A CS25 can be oriented toward the defining feature of a site. The product remains standardised. The experience becomes site-specific.</p>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-x-10 gap-y-10">
              {DESTINATIONS.map((d, i) => (
                <div key={d.name} className="border-t border-black/15 pt-5">
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono text-[10px] tracking-[0.24em] text-black/40 tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                    <span className="font-display text-2xl md:text-3xl tracking-tight text-black/90">{d.name}</span>
                  </div>
                  <p className="mt-3 text-[13px] leading-[1.75] text-black/60">{d.note}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-24">
              <PullQuote>One product. Many destinations.</PullQuote>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ————————————————— 05 · SENSITIVE LOCATIONS ————————————————— */}
      <section className="bg-[#F5F5F5] py-24 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <SectionNumber n={5} label="Can capsule houses work in sensitive locations?" />
            <h3 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02] max-w-3xl">
              Some of India&apos;s most attractive destinations<br/>
              <span className="italic text-black/40">are also its most regulated.</span>
            </h3>
          </Reveal>

          <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-12">
            <Reveal className="md:col-span-7">
              <div className="space-y-6 text-[16px] leading-[1.85] text-black/70">
                <p>National parks. Wildlife sanctuaries. Eco-Sensitive Zones. Coastal areas. Protected landscapes.</p>
                <p>Here, one distinction is essential:</p>
                <p className="font-display text-3xl md:text-4xl tracking-tight text-black/90 py-4">Modular does not mean exempt.</p>
                <p>A prefabricated or modular building does not automatically bypass environmental, wildlife, coastal or local planning regulations.</p>
                <p>Commercial tourist lodges and hotels inside wildlife sanctuaries are subject to specific approval requirements under India&apos;s Wildlife (Protection) Act. Similarly, coastal development is governed by India&apos;s Coastal Regulation Zone framework, with development controls depending on the site&apos;s classification and applicable planning framework.</p>
                <p>So the opportunity is not:</p>
                <p className="italic text-black/60">Build where construction is prohibited.</p>
                <p>It is:</p>
                <p className="italic text-black/85">Where hospitality development is legally permissible, can modular architecture provide a more controlled and site-sensitive way to deliver accommodation?</p>
                <p>That is where the concept becomes compelling.</p>
                <p className="text-[13px] text-black/50 pt-4 border-t border-black/10">
                  For remote or environmentally sensitive destinations, the wider project still needs to address land use, access, foundations, utilities, wastewater, ecological impact and all applicable approvals.
                </p>
              </div>
            </Reveal>

            <Reveal className="md:col-span-5">
              <div className="bg-white border border-black/10 p-8 md:p-10">
                  <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A] mb-4">Modular ≠ Exempt</div>
                  <div className="text-[13px] tracking-[0.16em] uppercase text-black/45 mb-6">Site</div>
                  <div className="text-[15px] text-black/85 mb-6">Is hospitality development permitted?</div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="border border-black/10 p-4">
                      <div className="text-[10px] tracking-[0.28em] uppercase text-black/40 mb-2">No</div>
                      <p className="text-[13px] leading-[1.65] text-black/70">Development cannot proceed merely because the unit is modular.</p>
                    </div>
                    <div className="border border-[#C7A36A]/60 bg-[#FAF6EE] p-4">
                      <div className="text-[10px] tracking-[0.28em] uppercase text-[#C7A36A] mb-2">Yes</div>
                      <p className="text-[13px] leading-[1.65] text-black/85">Assess site conditions, approvals, infrastructure and modular deployment.</p>
                    </div>
                  </div>

                  <div className="mt-8 space-y-3 text-[13px] tracking-[0.18em] uppercase text-black/60">
                    <div>Plan</div>
                    <ChevronDown size={14} className="text-black/25" />
                    <div>Manufacture</div>
                    <ChevronDown size={14} className="text-black/25" />
                    <div>Transport</div>
                    <ChevronDown size={14} className="text-black/25" />
                    <div>Install</div>
                  </div>
                </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ————————————————— 06 · FROM FACTORY TO RESORT ————————————————— */}
      <section className="py-24 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <SectionNumber n={6} label="From factory to resort" />
            <h3 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02] max-w-3xl">
              We make it.<br/>
              We transport it.<br/>
              <span className="italic text-black/40">We install it.</span>
            </h3>
          </Reveal>

          <Reveal>
            <div className="mt-14 max-w-3xl text-[16px] leading-[1.85] text-black/70 space-y-6">
              <p>One of the defining characteristics of modular construction is the separation between manufacturing and site work.</p>
              <p>A conventional room may require many construction activities to take place at the destination: structure → walls → services → finishes → bathroom → furniture.</p>
              <p>A modular approach can move a substantial portion of those activities into a controlled manufacturing environment. The objective isn&apos;t to eliminate construction. It is to change where and how the work happens.</p>
              <p>For remote hospitality projects, this can be particularly relevant because the site may have limited labour, difficult access or a short practical construction window.</p>
            </div>
          </Reveal>

          {/* Real photos: WIP → Transport/Install (crane) */}
          <div className="mt-20 md:mt-28 space-y-24 md:space-y-32">
            <Reveal>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center">
                <div className="md:col-span-1 hidden md:block font-mono text-[11px] tracking-[0.24em] text-black/35 tabular-nums">01 / MANUFACTURE</div>
                <div className="md:col-span-11">
                  <div className="relative w-full aspect-[16/9] overflow-hidden">
                    <img src={IMG.bedroomWip} loading="lazy" alt="CS25 Capsule Suite interior during manufacturing and finishing — factory-built modular accommodation" className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                  <Caption>CS25 interior during manufacturing and finishing.</Caption>
                </div>
              </div>
            </Reveal>

            <Reveal>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center">
                <div className="md:col-span-1 hidden md:block font-mono text-[11px] tracking-[0.24em] text-black/35 tabular-nums">02 / TRANSPORT</div>
                <div className="md:col-span-11">
                  <div className="relative w-full aspect-[16/9] overflow-hidden">
                    <img src={IMG.crane} loading="lazy" alt="Modularoom CS25 Capsule Suite being transported and positioned on site — modular resort installation" className="absolute inset-0 w-full h-full object-cover object-center" />
                  </div>
                  <Caption>CS25 being transported to its destination.</Caption>
                </div>
              </div>
            </Reveal>

            <Reveal>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center">
                <div className="md:col-span-1 hidden md:block font-mono text-[11px] tracking-[0.24em] text-black/35 tabular-nums">03 / INSTALLATION</div>
                <div className="md:col-span-11">
                  <div className="relative w-full aspect-[16/9] overflow-hidden">
                    <img src={IMG.crane} loading="lazy" alt="Modularoom CS25 Capsule Suite being positioned during modular installation" className="absolute inset-0 w-full h-full object-cover object-[center_35%] scale-[1.06]" />
                  </div>
                  <Caption>CS25 being positioned during modular installation.</Caption>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Factory → Site flow diagram */}
          <Reveal>
            <div className="mt-24 md:mt-32">
              <div className="text-[10px] tracking-[0.32em] uppercase text-black/45 mb-8">From factory to resort</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ApproachDiagram title="Factory" steps={['Design', 'Manufacture', 'Finish', 'Inspect']} />
                <ApproachDiagram mono title="Site" steps={['Prepare', 'Transport', 'Install', 'Connect']} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ————————————————— 07 · BUILDING A RESORT AROUND CS25 ————————————————— */}
      <section className="bg-[#FAF8F4] py-24 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <SectionNumber n={7} label="Building a resort around CS25" />
            <h3 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02] max-w-3xl">
              A resort as a system<br/>
              <span className="italic text-black/40">of architectural products.</span>
            </h3>
          </Reveal>

          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-12">
            <Reveal className="md:col-span-6">
              <div className="space-y-6 text-[16px] leading-[1.85] text-black/70">
                <p>A capsule resort does not need to look like a collection of identical rooms. The masterplan can use the same architectural product in different orientations and positions.</p>
                <p>Consider a conceptual 12-key CS25 resort. Twelve suites can be distributed around landscape, views, privacy, topography, pedestrian movement and shared amenities, with a central hospitality zone containing facilities such as reception, dining, pool and wellness spaces.</p>
                <p>The actual phasing depends on approvals, infrastructure, financing and demand. But modular accommodation naturally lends itself to a repeatable development model.</p>
                <p>The resort becomes a system of architectural products rather than one indivisible construction project.</p>
              </div>
            </Reveal>

            <Reveal className="md:col-span-6">
              <div>
                <div className="text-[10px] tracking-[0.32em] uppercase text-black/45 mb-6">Development sequence</div>
                <div className="space-y-4">
                  {[
                    ['Phase 01', '8 CS25 Suites'],
                    ['Phase 02', 'Additional accommodation'],
                    ['Phase 03', 'Restaurant + Pool + Wellness'],
                    ['Phase 04', 'Expanded hospitality'],
                  ].map(([p, v], i) => (
                    <React.Fragment key={p}>
                      <div className="grid grid-cols-[100px_1fr] md:grid-cols-[120px_1fr] gap-4 items-baseline border-b border-black/10 pb-4">
                        <div className="text-[11px] tracking-[0.28em] uppercase text-[#C7A36A]">{p}</div>
                        <div className="text-[16px] text-black/85">{v}</div>
                      </div>
                      {i < 3 && <ChevronDown size={14} strokeWidth={1} className="text-black/25 ml-2" />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          {/* Conceptual masterplan diagram (svg) */}
          <Reveal>
            <div className="mt-24 md:mt-32">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A] mb-6">Conceptual 12-key CS25 resort</div>
              <div className="bg-white border border-black/10 p-6 md:p-10">
                <ResortMasterplanSVG />
                <p className="mt-6 text-[11px] tracking-[0.18em] uppercase text-black/45">Conceptual masterplan</p>
                <p className="mt-2 max-w-2xl text-[12px] leading-[1.7] text-black/50 italic">
                  Illustrative only. Final resort layouts depend on site conditions, applicable regulations, infrastructure and project requirements.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ————————————————— 08 · COMPARISON ————————————————— */}
      <section className="py-24 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <SectionNumber n={8} label="Conventional vs modular resort development" />
            <h3 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02] max-w-3xl">
              Which approach is better<br/>
              <span className="italic text-black/40">suited to the site?</span>
            </h3>
          </Reveal>

          <Reveal>
            <div className="mt-12 max-w-3xl text-[16px] leading-[1.85] text-black/70 space-y-6">
              <p>The comparison isn&apos;t simply about which construction method costs less. The more useful question is: which development approach is better suited to the site and hospitality concept?</p>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-16 md:mt-20 overflow-x-auto">
              <table className="w-full min-w-[720px] border-collapse text-[14px] md:text-[15px]">
                <thead>
                  <tr className="text-[10px] tracking-[0.28em] uppercase text-black/45">
                    <th className="text-left font-normal py-4 pr-6 border-b border-black/25 w-1/4"> </th>
                    <th className="text-left font-normal py-4 pr-6 border-b border-black/25">Conventional construction</th>
                    <th className="text-left font-normal py-4 pr-6 border-b border-black/25 text-[#C7A36A]">Modular capsule suite</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARE.map(([k, a, b]) => (
                    <tr key={k} className="align-top">
                      <td className="py-5 pr-6 border-b border-black/10 text-[10px] tracking-[0.28em] uppercase text-black/50">{k}</td>
                      <td className="py-5 pr-6 border-b border-black/10 text-black/70">{a}</td>
                      <td className="py-5 pr-6 border-b border-black/10 text-black/85">{b}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-16 max-w-3xl text-[16px] leading-[1.85] text-black/70 space-y-4">
              <p>Neither approach is universally better. A modular system is most compelling when its manufacturing, transportation, installation and operational advantages align with the project&apos;s requirements.</p>
              <p>And importantly: a modular building remains subject to the regulations applicable to its location and intended use.</p>
            </div>
          </Reveal>

          {/* Two paths to 10 resort keys */}
          <Reveal>
            <div className="mt-24">
              <div className="text-[10px] tracking-[0.32em] uppercase text-black/45 mb-8">Two paths to 10 resort keys</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ApproachDiagram
                  title="Conventional"
                  steps={['Site', 'Structure', 'Envelope', 'Services', 'Interiors', '10 finished rooms']}
                />
                <ApproachDiagram
                  mono
                  title="Modular"
                  steps={['Site preparation · 10 × CS25 manufactured', 'Transport', 'Installation', 'Connections', '10 finished suites']}
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ————————————————— 09 · FUTURE OF DESTINATION HOSPITALITY ————————————————— */}
      <section className="relative bg-black text-white">
        <div className="relative w-full" style={{ minHeight: '80svh' }}>
          <img
            src={IMG.nature}
            alt="Modularoom CS25 Capsule Suite in a destination landscape — the future of modular resort accommodation in India"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/50" />
          <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-40 min-h-[80svh] flex flex-col justify-end">
            <Reveal>
              <h2 className="flex items-baseline gap-6 mb-8 font-normal">
                <span className="font-display text-[64px] md:text-[92px] leading-none tracking-tight text-white/15 tabular-nums">09</span>
                <span className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">
                  <span className="sr-only">09 — </span>The future of destination hospitality
                </span>
              </h2>
              <h3 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] max-w-4xl">
                Where the landscape<br/><span className="italic text-white/60">remains the hero.</span>
              </h3>
              <div className="mt-10 max-w-2xl text-[16px] md:text-[17px] leading-[1.85] text-white/75 space-y-5">
                <p>India has extraordinary destinations. The opportunity is not simply to put more rooms into them. It is to create better relationships between accommodation and place.</p>
                <p className="text-white/85 italic">
                  A bedroom facing a mountain. A balcony overlooking a forest. A private suite beside a vineyard. A retreat surrounded by farmland.
                </p>
                <p>This is where capsule houses become interesting. Not because they are small. Not because they are unusual. But because they can turn accommodation into a repeatable architectural product that can be positioned around the experience a destination is trying to create.</p>
                <p>CS25 is Modularoom&apos;s expression of that idea: one bedroom, one bathroom, one private balcony — a Capsule Suite designed for resorts, retreats and destination hospitality.</p>
                <p className="text-[#C7A36A] font-display text-2xl md:text-3xl tracking-tight not-italic mt-8">
                  The room is the product. The landscape is the destination.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ————————————————— CTA ————————————————— */}
      <section className="py-24 md:py-40 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <div className="max-w-3xl">
              <div className="font-mono text-[11px] tracking-[0.24em] text-black/45 mb-6">CS25</div>
              <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02]">
                One bedroom.<br/>
                One bathroom.<br/>
                <span className="italic text-black/40">One private balcony.</span>
              </h2>
              <p className="mt-8 max-w-xl text-[15px] md:text-[17px] leading-[1.75] text-black/60">
                A Capsule Suite designed for resorts, retreats and destination hospitality. <Link to="/hospitality" className="text-black underline decoration-[#C7A36A]/60 underline-offset-4 hover:text-[#C7A36A] transition-colors">Explore modular hospitality</Link> or <Link to="/gallery" className="text-black underline decoration-[#C7A36A]/60 underline-offset-4 hover:text-[#C7A36A] transition-colors">explore Modularoom projects</Link>.
              </p>
              <div className="mt-12 flex flex-wrap gap-4">
                <Link
                  to="/products/cs25"
                  data-testid="cta-explore-cs25"
                  className="inline-flex items-center gap-3 rounded-full bg-[#111] text-white px-8 py-4 text-[11px] tracking-[0.24em] uppercase hover:bg-[#C7A36A] transition-colors"
                >
                  Explore CS25 <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
                <Link
                  to="/contact#book"
                  data-testid="cta-discuss-resort"
                  className="inline-flex items-center gap-3 rounded-full border border-black/25 text-black/85 px-8 py-4 text-[11px] tracking-[0.24em] uppercase hover:bg-[#111] hover:text-white hover:border-[#111] transition-colors"
                >
                  Discuss a resort project <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
              </div>

              {/* Share strip — end of article */}
              <div className="mt-14 pt-8 border-t border-black/10">
                <ShareBar
                  url={ARTICLE_URL}
                  title="CS25: A Capsule House for Resort Hospitality in India"
                  testid="share-bottom"
                  variant="light"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ————————————————— FAQ ————————————————— */}
      <section className="bg-[#F5F5F5] py-24 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A] mb-6">Frequently asked</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.02] max-w-3xl">
              Capsule houses, resorts<br/>
              <span className="italic text-black/40">and what they can (and can&apos;t) do.</span>
            </h2>
          </Reveal>

          <div className="mt-14 max-w-4xl">
            {FAQS.map((f, i) => (
              <FaqItem key={f.q} q={f.q} a={f.a} idx={i} />
            ))}
          </div>

          <Reveal>
            <div className="mt-16 max-w-3xl text-[13px] leading-[1.85] text-black/55">
              <p>Learn more about <Link to="/collections" className="underline decoration-[#C7A36A]/60 underline-offset-4 hover:text-[#C7A36A] transition-colors">Modularoom Capsule Suites</Link>, <Link to="/products/cs25" className="underline decoration-[#C7A36A]/60 underline-offset-4 hover:text-[#C7A36A] transition-colors">discover the CS25 Capsule Suite</Link>, or <Link to="/contact#book" className="underline decoration-[#C7A36A]/60 underline-offset-4 hover:text-[#C7A36A] transition-colors">discuss your resort project</Link>.</p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

// ————————————————————————————————————————————————————————
// Inline SVG — Conceptual 12-key CS25 resort masterplan.
// Pure line-work in the Modularoom palette: black · warm off-white · champagne gold.
// ————————————————————————————————————————————————————————
function ResortMasterplanSVG() {
  const units = [
    { x: 90,  y: 110, r: -18 },
    { x: 165, y: 90,  r: -6  },
    { x: 240, y: 105, r: 8   },
    { x: 320, y: 130, r: 22  },
    { x: 95,  y: 200, r: -32 },
    { x: 190, y: 220, r: 4   },
    { x: 300, y: 210, r: 18  },
    { x: 620, y: 105, r: -14 },
    { x: 700, y: 130, r: 6   },
    { x: 780, y: 110, r: -22 },
    { x: 640, y: 220, r: 12  },
    { x: 750, y: 240, r: -8  },
  ];
  return (
    <svg viewBox="0 0 900 380" className="w-full h-auto" role="img" aria-label="Conceptual 12-key CS25 resort masterplan">
      {/* Site outline */}
      <rect x="20" y="20" width="860" height="340" fill="#FAF8F4" stroke="#111" strokeWidth="1" />
      {/* Contour lines (landscape suggestion) */}
      <g stroke="#111" strokeOpacity="0.08" fill="none" strokeWidth="1">
        <path d="M20,60 C 200,80 500,40 880,70" />
        <path d="M20,120 C 220,150 520,90 880,130" />
        <path d="M20,190 C 240,220 540,160 880,200" />
        <path d="M20,260 C 260,290 560,230 880,270" />
        <path d="M20,320 C 240,340 540,290 880,330" />
      </g>
      {/* Pedestrian paths */}
      <g stroke="#C7A36A" strokeOpacity="0.55" fill="none" strokeDasharray="3 6" strokeWidth="1.25">
        <path d="M450,180 C 350,120 220,110 90,110" />
        <path d="M450,180 C 350,240 220,220 100,220" />
        <path d="M450,180 C 560,110 690,110 780,110" />
        <path d="M450,180 C 560,240 700,240 780,240" />
      </g>
      {/* Central hospitality zone */}
      <g>
        <rect x="410" y="150" width="80" height="60" fill="#111" />
        <text x="450" y="184" textAnchor="middle" fill="#C7A36A" fontSize="8" letterSpacing="2" fontFamily="ui-monospace, monospace">CENTRAL</text>
        <text x="450" y="196" textAnchor="middle" fill="#ffffff" fontSize="7" letterSpacing="1.5" fontFamily="ui-monospace, monospace">HOSPITALITY</text>
      </g>
      {/* Pool */}
      <ellipse cx="450" cy="260" rx="36" ry="14" fill="none" stroke="#C7A36A" strokeWidth="1" />
      <text x="450" y="263" textAnchor="middle" fill="#111" fontSize="7" letterSpacing="1.5" fontFamily="ui-monospace, monospace">POOL</text>
      {/* Reception marker */}
      <circle cx="450" cy="120" r="4" fill="#C7A36A" />
      <text x="450" y="108" textAnchor="middle" fill="#111" fontSize="7" letterSpacing="1.5" fontFamily="ui-monospace, monospace">RECEPTION</text>
      {/* 12 CS25 units */}
      {units.map((u, i) => (
        <g key={i} transform={`translate(${u.x},${u.y}) rotate(${u.r})`}>
          <rect x="-18" y="-9" width="36" height="18" fill="#ffffff" stroke="#111" strokeWidth="1" />
          <rect x="6" y="-8" width="11" height="16" fill="#111" opacity="0.85" />
          <line x1="-18" y1="9" x2="18" y2="9" stroke="#C7A36A" strokeWidth="1" />
          <text x="-15" y="-12" fill="#111" fontSize="6" letterSpacing="1.5" fontFamily="ui-monospace, monospace">CS25 · {String(i + 1).padStart(2, '0')}</text>
        </g>
      ))}
      {/* Labels */}
      <text x="30" y="45" fill="#111" fontSize="8" letterSpacing="2" fontFamily="ui-monospace, monospace">N ↑</text>
      <text x="870" y="45" textAnchor="end" fill="#111" fontSize="8" letterSpacing="2" fontFamily="ui-monospace, monospace">CONCEPTUAL SITE PLAN</text>
      <text x="30" y="352" fill="#111" fontSize="7" letterSpacing="1.6" fontFamily="ui-monospace, monospace" opacity="0.6">12 × CS25 · pool · reception · central hospitality · pedestrian paths</text>
    </svg>
  );
}
